'use client';

import React, { useContext, useEffect, useState, type CSSProperties, type Ref } from 'react';
import { Button, type BaseButtonProps } from '@milanote/roma/components/base/buttons/button';
import { cx } from '@milanote/roma/utils/cx';
import {
    AttentionFlashButtonContext,
    AttentionFlashButtonProvider,
    useAttentionFlashButtonController,
} from './attention-flash-button-context';

export type AttentionFlashButtonProps = Omit<BaseButtonProps, 'ref'> & {
    /** Total duration of the attention flash in milliseconds. Default: 400 */
    flashDuration?: number;
    /** Optional background color used at the peak of each flash. */
    flashColor?: string;
};

const DEFAULT_FLASH_DURATION = 400;
const FLASH_ITERATION_COUNT = 2;

type AttentionFlashStyle = CSSProperties & {
    '--button-attention-flash-color'?: string;
};

type AttentionFlashButtonComponent = React.ForwardRefExoticComponent<
    AttentionFlashButtonProps & React.RefAttributes<HTMLButtonElement>
> & {
    Provider: typeof AttentionFlashButtonProvider;
    useController: typeof useAttentionFlashButtonController;
};

/** Returns the per-iteration CSS duration needed to make the full two-pulse flash match the requested duration. */
const getFlashIterationDuration = (flashDuration: number) => `${flashDuration / FLASH_ITERATION_COUNT}ms`;

/** Renders a Roma Button that can replay a root-level attention flash when its context controller is triggered. */
const AttentionFlashButtonRoot = React.forwardRef<HTMLButtonElement, AttentionFlashButtonProps>(
    ({ flashDuration = DEFAULT_FLASH_DURATION, flashColor, className, style, ...buttonProps }, ref) => {
        const controller = useContext(AttentionFlashButtonContext);
        const [isFlashing, setIsFlashing] = useState(false);

        useEffect(() => {
            if (!controller?.triggerKey) return;

            setIsFlashing(false);

            const replayTimer = setTimeout(() => setIsFlashing(true), 0);
            const endTimer = setTimeout(() => setIsFlashing(false), flashDuration);

            return () => {
                clearTimeout(replayTimer);
                clearTimeout(endTimer);
            };
        }, [controller?.triggerKey, flashDuration]);

        const attentionFlashStyle: AttentionFlashStyle = {
            animationDuration: getFlashIterationDuration(flashDuration),
            ['--button-attention-flash-color' as string]: flashColor,
            ...style,
        };

        return (
            <Button
                {...buttonProps}
                ref={ref as Ref<HTMLButtonElement | HTMLAnchorElement>}
                className={cx(isFlashing && 'animate-button-attention-flash', className)}
                style={attentionFlashStyle}
            />
        );
    },
);

AttentionFlashButtonRoot.displayName = 'AttentionFlashButton';

export const AttentionFlashButton = AttentionFlashButtonRoot as AttentionFlashButtonComponent;
AttentionFlashButton.Provider = AttentionFlashButtonProvider;
AttentionFlashButton.useController = useAttentionFlashButtonController;

export type {
    AttentionFlashButtonController,
    AttentionFlashButtonProviderProps,
} from './attention-flash-button-context';
